import React, { useState, useEffect } from 'react';
import {
    Search,
    Coins,
    ExternalLink
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { bondsAPI, formatCurrency, formatDate, formatCompactCurrency } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { createResponseSelector } from '../../utils/apiHelpers';

const BondHoldings = () => {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const PAGE_SIZE = 50; // mirror BondManagement

    // Reset to first page whenever filters / search change
    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearchTerm, statusFilter, typeFilter]);

    // Debounce search term
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500);

        return () => {
            clearTimeout(handler);
        };
    }, [searchTerm]);

    // Fetch bonds data
    const { data: bondsData, isPending: bondsLoading, error: bondsError } = useQuery({
        queryKey: ['bonds-holdings', debouncedSearchTerm, statusFilter, typeFilter, currentPage],
        queryFn: () => bondsAPI.getBonds({
            search: debouncedSearchTerm || undefined,
            status: statusFilter === 'all' ? undefined : statusFilter,
            productType: typeFilter === 'all' ? undefined : typeFilter,
            page: currentPage,
            limit: PAGE_SIZE
        })
    });

    // Derive bonds and totals similar to BondManagement
    const respRoot = bondsData?.data?.data ?? bondsData?.data ?? bondsData;
    const bonds = Array.isArray(respRoot?.bonds)
        ? respRoot.bonds
        : Array.isArray(respRoot?.data)
            ? respRoot.data
            : Array.isArray(respRoot?.items)
                ? respRoot.items
                : Array.isArray(respRoot?.docs)
                    ? respRoot.docs
                    : Array.isArray(respRoot?.results)
                        ? respRoot.results
                        : Array.isArray(respRoot)
                            ? respRoot
                            : [];

    const totalBonds = typeof respRoot?.total === 'number'
        ? respRoot.total
        : typeof respRoot?.count === 'number'
            ? respRoot.count
            : typeof respRoot?.pagination?.total === 'number'
                ? respRoot.pagination.total
                : bonds.length;

    const totalPages = Math.max(1, Math.ceil((totalBonds || bonds.length) / PAGE_SIZE));

    const getStatusBadge = (status) => {
        const statusClasses = {
            'Draft': 'badge-secondary',
            'Pending L1': 'badge-warning',
            'Pending L2': 'badge-warning',
            'Pending Review': 'badge-warning',
            'Approved': 'badge-success',
            'Rejected': 'badge-error',
            'Listed': 'badge-success',
            'Active': 'badge-success',
            'Ready for Allocation': 'badge-info',
            'Allocation Uploaded': 'badge-info',
            'Allocation Finalized': 'badge-info',
            'Redemption in Process': 'badge-warning',
            'Redemption Completed': 'badge-success',
            'Burn in Process': 'badge-error',
            'Burn Completed': 'badge-neutral',
            'Matured': 'badge-info'
        };

        const statusIcons = {
            'Draft': '📝',
            'Pending L1': '①',
            'Pending L2': '②',
            'Pending Review': '⏳',
            'Approved': '✅',
            'Rejected': '❌',
            'Listed': '🏛️',
            'Active': '✅',
            'Ready for Allocation': '📋',
            'Allocation Uploaded': '📤',
            'Allocation Finalized': '🎯',
            'Redemption in Process': '🔄',
            'Redemption Completed': '✅',
            'Burn in Process': '🔥',
            'Burn Completed': '⚫',
            'Matured': '🏁'
        };

        return (
            <span className={`badge ${statusClasses[status] || 'badge-secondary'}`}>
                <span className="mr-1">{statusIcons[status]}</span>
                {status}
            </span>
        );
    };

    // NOTE: Local filtering retained for table display, but summary widgets removed.
    const filteredBonds = bonds.filter(bond => {
        if (!searchTerm) return true;
        const haystack = [bond.bondName, bond.isin, bond.productType, bond.status].filter(Boolean).join(' ').toLowerCase();
        return haystack.includes(searchTerm.toLowerCase());
    }).filter(bond => (statusFilter === 'all' ? true : bond.status === statusFilter));

    if (bondsLoading) {
        return <LoadingSpinner size="lg" text="Loading bond holdings..." />;
    }

    if (bondsError) {
        console.error('Bonds error:', bondsError);
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center py-4">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Bond Holdings</h1>
                            <p className="text-sm text-gray-500">Monitor your issued bonds' token performance and holdings distribution</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

                {/* Summary widgets removed per request */}

                {/* Filters and Search */}
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                    {/* Search Card - 60% width (3 out of 5 columns) */}
                    <div className="card lg:col-span-3">
                        <div className="card-body">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-dark-gray w-4 h-4" />
                                <input
                                    type="text"
                                    placeholder="Search by bond name, ISIN, or token symbol..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="form-input pl-10"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Status Filter Card - 20% width (1 out of 5 columns) */}
                    <div className="card lg:col-span-1">
                        <div className="card-body">
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="form-input"
                            >
                                <option value="all">All Status</option>
                                <option value="Active">Active</option>
                                <option value="Listed">Listed</option>
                                <option value="Pending Review">Pending Review</option>
                                <option value="Approved">Approved</option>
                                <option value="Draft">Draft</option>
                                <option value="Matured">Matured</option>
                            </select>
                        </div>
                    </div>

                    {/* Type Filter Card - 20% width (1 out of 5 columns) */}
                    <div className="card lg:col-span-1">
                        <div className="card-body">
                            <select
                                value={typeFilter}
                                onChange={(e) => setTypeFilter(e.target.value)}
                                className="form-input"
                            >
                                <option value="all">All Types</option>
                                <option value="Fixed Notes">Conventional</option>
                                <option value="Sukuk">Sukuk</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Bond Holdings Table */}
                <div className="card mt-8">
                    <div className="card-header">
                        <div className="flex items-center justify-between">
                            <h2 className="text-lg font-medium text-secondary-900">
                                Bond Holdings ({totalBonds})
                            </h2>
                            <div className="flex items-center space-x-2">
                                <span className="text-sm text-secondary-600">
                                    {searchTerm && `Filtered by "${searchTerm}"`}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="table">
                            <thead className="table-header">
                                <tr>
                                    <th className="table-header-cell">Bond Details</th>
                                    <th className="table-header-cell">Status</th>
                                    <th className="table-header-cell">Token Holders</th>
                                    <th className="table-header-cell">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="table-body">
                                {filteredBonds.length > 0 ? (
                                    filteredBonds.map((bond) => {
                                        return (
                                            <tr key={bond._id} className="table-row">
                                                <td className="table-cell">
                                                    <div>
                                                        <div className="flex items-center space-x-2">
                                                            <div className="font-medium text-secondary-900">{bond.bondName}</div>
                                                            <span className="px-2 py-1 text-xs font-medium bg-primary-100 text-primary-800 rounded">
                                                                {bond.tokenDetails?.symbol || '—'}
                                                            </span>
                                                        </div>
                                                        <div className="text-sm text-secondary-500 mt-1">
                                                            {bond.productType === "Fixed Notes" ? "Conventional" : bond.productType} • {bond.isin || 'Pending ISIN'}
                                                        </div>
                                                        {bond.bondNumber && (
                                                            <div className="text-xs text-secondary-600 mt-1 font-mono">
                                                                Bond #: {bond.bondNumber}
                                                            </div>
                                                        )}
                                                        <div className="text-xs mt-1" style={{ color: '#6b7280' }}>
                                                            Issued: {formatDate(bond.createdAt)}
                                                        </div>
                                                    </div>
                                                </td>

                                                <td className="table-cell">
                                                    <div>
                                                        {getStatusBadge(bond.status)}
                                                        {bond.submittedAt && (
                                                            <div className="text-xs text-secondary-400 mt-1">
                                                                Submitted: {formatDate(bond.submittedAt)}
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="table-cell">
                                                    <div className="text-sm font-medium text-secondary-900">
                                                        {typeof bond.tokenHolders === 'number' ? bond.tokenHolders : '—'}
                                                    </div>
                                                    <div className="text-xs text-secondary-500 mt-0.5">Allocation entries</div>
                                                </td>
                                                <td className="table-cell">
                                                    <div className="flex items-center space-x-2">
                                                        <button
                                                            onClick={() => navigate(`/issuer/bond-holders/${bond._id}`)}
                                                            
                                                            className="btn-primary text-xs px-3 py-1 disabled:opacity-50 disabled:cursor-not-allowed"
                                                        >
                                                            View Details
                                                        </button>
                                                        {(() => {
                                                            const tokenAddress = bond?.tokenDetails?.tokenAddress || bond?.tokenDetails?.contractAddress;
                                                            const explorerBase = 'https://teleskop.aeris-dev.codedsolution-web3.com/address/';
                                                            if (!tokenAddress) {
                                                                return <span className="text-xs text-gray-400" title="Token not yet created">—</span>;
                                                            }
                                                            return (
                                                                <a
                                                                    href={`${explorerBase}${tokenAddress}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="text-secondary-600 hover:text-secondary-700 p-1"
                                                                    title="View on Explorer"
                                                                >
                                                                    <ExternalLink className="w-4 h-4" />
                                                                </a>
                                                            );
                                                        })()}
                                                        {bond.status === 'Active' && (
                                                            <button
                                                                onClick={() => navigate(`/issuer/funding?bondId=${bond._id}`)}
                                                                className="text-success-600 hover:text-success-700 p-1"
                                                                title="Manage Token"
                                                            >
                                                                <Coins className="w-4 h-4" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="4" className="table-cell text-center py-12">
                                            <div className="text-secondary-500">
                                                <Coins className="w-12 h-12 text-secondary-300 mx-auto mb-4" />
                                                <p className="text-lg font-medium mb-2">
                                                    {searchTerm ? 'No bonds found' : 'No bond holdings yet'}
                                                </p>
                                                <p className="text-sm mb-4">
                                                    {searchTerm
                                                        ? `No bonds match your search "${searchTerm}"`
                                                        : 'Create and issue bonds to see your bond holdings here'
                                                    }
                                                </p>
                                                {!searchTerm && (
                                                    <button
                                                        onClick={() => navigate('/issuer/bonds/create')}
                                                        className="btn-primary"
                                                    >
                                                        Create First Bond
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    {/* Pagination Controls */}
                    <div className="flex flex-col sm:flex-row items-center justify-between px-4 py-4 border-t gap-4">
                        <div className="text-sm text-secondary-600">
                            Showing page {currentPage} of {totalPages} ({totalBonds} bonds)
                        </div>
                        <div className="flex items-center space-x-2">
                            <button
                                className="px-2 py-1 rounded border text-sm disabled:opacity-50"
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                            >Prev</button>
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                <button
                                    key={page}
                                    className={`px-2 py-1 rounded border text-sm mx-0.5 ${page === currentPage ? 'bg-blue-600 text-white' : 'bg-white text-blue-600'}`}
                                    onClick={() => setCurrentPage(page)}
                                    disabled={page === currentPage}
                                >{page}</button>
                            ))}
                            <button
                                className="px-2 py-1 rounded border text-sm disabled:opacity-50"
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                            >Next</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BondHoldings;
